import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader, StatusBadge } from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Search, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDate, fmtRelative } from "@/lib/format";
import { toast } from "sonner";

export default function Jobs() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (status !== "all") params.status = status;
    const r = await api.get("/jobs", { params });
    setRows(r.data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [status]);

  const toggleOne = (id) => setSelected((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAllOnPage = () => setSelected((s) => {
    const allIds = rows.map((j) => j.job_id);
    const allSelected = allIds.every((id) => s.has(id));
    if (allSelected) {
      const n = new Set(s); allIds.forEach((id) => n.delete(id)); return n;
    }
    const n = new Set(s); allIds.forEach((id) => n.add(id)); return n;
  });
  const clearSelection = () => setSelected(new Set());
  const bulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const r = await api.post("/jobs/bulk-delete", { ids: Array.from(selected) });
      toast.success(`Deleted ${r.data.deleted} job${r.data.deleted === 1 ? "" : "s"}`);
      clearSelection();
      setBulkDeleteOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk delete failed");
    } finally { setBulkDeleting(false); }
  };

  return (
    <div>
      <PageHeader testid="page-jobs" title="Jobs & deadlines" subtitle={`${rows.length} shown · sorted by deadline`} />
      <div className="p-6 lg:p-8 space-y-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="jobs-search" className="pl-9 w-80 bg-white" placeholder="Search titles…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="jobs-status-filter" className="w-40 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="not_started">Not started</SelectItem>
              <SelectItem value="in_progress">In progress</SelectItem>
              <SelectItem value="on_hold">On hold</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} className="rounded-md">Apply</Button>
        </div>

        <div className="border border-slate-200 rounded-md bg-white overflow-hidden">
          {selected.size > 0 && (
            <div data-testid="jobs-bulk-bar" className="bg-slate-900 text-slate-100 px-4 py-2 flex items-center justify-between text-sm">
              <span data-testid="jobs-selected-count">
                <span className="font-semibold">{selected.size}</span> selected
              </span>
              <div className="flex items-center gap-3">
                <button type="button" onClick={clearSelection} className="text-slate-300 hover:text-white text-xs" data-testid="jobs-clear-selection">Clear</button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setBulkDeleteOpen(true)}
                  className="h-8"
                  data-testid="jobs-bulk-delete-btn"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete selected
                </Button>
              </div>
            </div>
          )}
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-[0.1em] text-slate-500">
              <tr>
                <th className="w-10 px-4 py-2">
                  <Checkbox
                    data-testid="jobs-select-all"
                    checked={rows.length > 0 && rows.every((j) => selected.has(j.job_id))}
                    onCheckedChange={toggleAllOnPage}
                    aria-label="Select all jobs on this page"
                  />
                </th>
                <th className="text-left px-4 py-2">Job</th>
                <th className="text-left px-4 py-2">Client</th>
                <th className="text-left px-4 py-2">Stage</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Deadline</th>
                <th className="text-left px-4 py-2">Relative</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500">No jobs to show.</td></tr>}
              {rows.map((j) => (
                <tr key={j.job_id} data-testid="job-row" className={`border-t border-slate-100 hover:bg-slate-50 transition-colors ${selected.has(j.job_id) ? "bg-slate-50" : ""}`}>
                  <td className="px-4 py-3">
                    <Checkbox
                      data-testid={`job-row-select-${j.job_id}`}
                      checked={selected.has(j.job_id)}
                      onCheckedChange={() => toggleOne(j.job_id)}
                      aria-label={`Select ${j.title}`}
                    />
                  </td>
                  <td className="px-4 py-3"><Link to={`/jobs/${j.job_id}`} className="font-medium text-slate-900 hover:text-blue-600 transition-colors">{j.title}</Link></td>
                  <td className="px-4 py-3"><Link to={`/clients/${j.client_id}`} className="text-slate-600 hover:text-blue-600 transition-colors">{j.client_name}</Link></td>
                  <td className="px-4 py-3 text-slate-600">{j.stage}</td>
                  <td className="px-4 py-3"><StatusBadge status={j.status} /></td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-800">{fmtDate(j.deadline)}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{fmtRelative(j.deadline)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent data-testid="jobs-bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selected.size} job{selected.size === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>
              This also removes tasks and reminders attached to
              {selected.size === 1 ? " this job" : " these jobs"}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={bulkDelete}
              disabled={bulkDeleting}
              data-testid="jobs-bulk-delete-confirm"
              className="bg-rose-600 hover:bg-rose-700"
            >
              {bulkDeleting ? "Deleting…" : `Yes, delete ${selected.size}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
